import { useEffect, useState } from "react";

import { useNotification } from "../../../hooks";
import { useCart, useInspectionAttributes } from "../../../hooks/query";
import { serializeMeasurementAttributeValue } from "../../../utils/measurementAttribute";
import type { LineEdit, LaborLineEdit } from "../types";
import type { MaterialRowItem } from "../utils/materialLineItems";
import {
    buildCartItemPayload,
    buildCartUpdatePayload,
    buildLaborCartItemPayload,
    buildLaborCartUpdatePayload,
    cloneLaborEdit,
    cloneMaterialEdit,
    getProductCategoryForEditKey,
    hasLaborChanges,
    hasMaterialChanges,
    identifyLaborChanges,
    identifyMaterialChanges,
} from "../utils/cartMapping";
import { useEstimateContractContextManager } from "./";

/** Scalar inspection attrs edited from the Materials tab (not measurement JSON). */
const SYNCABLE_SCALAR_ATTRIBUTES = ["RoofColor"] as const;

export function useSync({
    projectId,
    materialsByGroup = {},
}: {
    projectId?: string;
    materialsByGroup?: Record<string, MaterialRowItem[]>;
} = {}) {
    const { values, referenceValue, setReferenceValue, initLoading } =
        useEstimateContractContextManager();

    const { mutateAsync: createAttribute } =
        useInspectionAttributes().useCreate();
    const { mutateAsync: updateAttribute } =
        useInspectionAttributes().useUpdateSingleAttribute();
    const { useCreate, useUpdate, useDelete } = useCart();
    const { mutateAsync: createCartItems } = useCreate();
    const { mutateAsync: updateCartItem } = useUpdate();
    const { mutateAsync: deleteCartItem } = useDelete();
    const { showSuccess, showError } = useNotification();

    const [changeIdentified, setChangeIdentified] = useState(false);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        if (initLoading) {
            setChangeIdentified(false);
            return;
        }

        setChangeIdentified(
            hasMeasurementChanges(
                values.measurements,
                referenceValue.measurements,
            ) ||
                hasMaterialChanges(
                    values.materials,
                    referenceValue.materials,
                ) ||
                hasLaborChanges(values.labor, referenceValue.labor) ||
                hasScalarAttributeChanges(
                    values.attributes,
                    referenceValue.attributes,
                ),
        );
    }, [initLoading, values]);

    async function flushSave() {
        if (!projectId) {
            return { success: false };
        }

        const measurementChanges = identifyMeasurementChanges(
            values.measurements,
            referenceValue.measurements,
        );
        const materialChanges = identifyMaterialChanges(
            values.materials,
            referenceValue.materials,
        );
        const laborChanges = identifyLaborChanges(
            values.labor,
            referenceValue.labor,
        );
        const scalarAttributeChanges = identifyScalarAttributeChanges(
            values.attributes,
            referenceValue.attributes,
        );

        if (
            Object.keys(measurementChanges).length === 0 &&
            Object.keys(materialChanges).length === 0 &&
            Object.keys(laborChanges).length === 0 &&
            Object.keys(scalarAttributeChanges).length === 0
        ) {
            return { success: true };
        }

        setSyncing(true);

        try {
            const syncedMeasurements: Record<string, any> = {};
            const syncedAttributeIds = {
                ...(referenceValue.attributeIds || {}),
            };

            for (const [key, { to }] of Object.entries(measurementChanges)) {
                const value = serializeMeasurementAttributeValue(to as any);
                const existingId = syncedAttributeIds[key];

                if (existingId) {
                    await updateAttribute({
                        Id: existingId,
                        Value: value,
                    });
                } else {
                    const created: any = await createAttribute({
                        ProjectId: projectId,
                        AttributeName: key,
                        Value: value,
                    });
                    if (created?.Id) {
                        syncedAttributeIds[key] = created.Id;
                    }
                }

                syncedMeasurements[key] = cloneMeasurement(to);
            }

            const syncedAttributes: Record<string, unknown> = {};

            for (const [key, { to }] of Object.entries(
                scalarAttributeChanges,
            )) {
                const existingId = syncedAttributeIds[key];
                if (existingId) {
                    await updateAttribute({
                        Id: existingId,
                        Value: to,
                    });
                } else {
                    const created: any = await createAttribute({
                        ProjectId: projectId,
                        AttributeName: key,
                        Value: to,
                    });
                    if (created?.Id) {
                        syncedAttributeIds[key] = created.Id;
                    }
                }
                syncedAttributes[key] = to;
            }

            const syncedMaterials: Record<string, LineEdit> = {};
            const syncedLabor: Record<string, LaborLineEdit> = {};
            const syncedCartIds = { ...(referenceValue.cartIds || {}) };

            for (const [editKey, { from, to }] of Object.entries(
                materialChanges,
            )) {
                const edit = to;
                const previous = from;
                const cartId = referenceValue.cartIds?.[editKey];

                if (!edit?.selectedProductId) {
                    if (cartId) {
                        await deleteCartItem({ Id: cartId });
                        delete syncedCartIds[editKey];
                    }
                    continue;
                }

                const sameProduct =
                    cartId &&
                    previous?.selectedProductId === edit.selectedProductId;

                if (sameProduct) {
                    await updateCartItem(buildCartUpdatePayload(edit, cartId));
                    syncedMaterials[editKey] = cloneMaterialEdit(edit);
                    continue;
                }

                if (cartId) {
                    await deleteCartItem({ Id: cartId });
                    delete syncedCartIds[editKey];
                }

                const category = getProductCategoryForEditKey(
                    materialsByGroup,
                    editKey,
                );
                const created = await createCartItems({
                    ProjectId: projectId,
                    ...(values.estimateOptionId
                        ? { EstimateOptionId: values.estimateOptionId }
                        : {}),
                    CartItems: [buildCartItemPayload(edit, category)],
                });

                const createdItem = Array.isArray(created) ? created[0] : null;
                const newCartId = createdItem?.Id ?? createdItem?.id;
                if (newCartId) {
                    syncedCartIds[editKey] = newCartId;
                }

                syncedMaterials[editKey] = cloneMaterialEdit(edit);
            }

            for (const [editKey, { from, to }] of Object.entries(
                laborChanges,
            )) {
                const edit = to;
                const previous = from;
                if (!edit?.laborServiceId) continue;

                const cartId = referenceValue.cartIds?.[editKey];
                const sameService =
                    cartId && previous?.laborServiceId === edit.laborServiceId;

                if (sameService) {
                    await updateCartItem(
                        buildLaborCartUpdatePayload(edit, cartId),
                    );
                    syncedLabor[editKey] = cloneLaborEdit(edit);
                    continue;
                }

                if (cartId) {
                    await deleteCartItem({ Id: cartId });
                    delete syncedCartIds[editKey];
                }

                const created = await createCartItems({
                    ProjectId: projectId,
                    ...(values.estimateOptionId
                        ? { EstimateOptionId: values.estimateOptionId }
                        : {}),
                    CartItems: [buildLaborCartItemPayload(edit)],
                });

                const createdItem = Array.isArray(created) ? created[0] : null;
                const newCartId = createdItem?.Id ?? createdItem?.id;
                if (newCartId) {
                    syncedCartIds[editKey] = newCartId;
                }

                syncedLabor[editKey] = cloneLaborEdit(edit);
            }

            setReferenceValue((prev: any) => ({
                ...prev,
                measurements: {
                    ...prev.measurements,
                    ...syncedMeasurements,
                },
                attributes: {
                    ...prev.attributes,
                    ...syncedAttributes,
                },
                attributeIds: syncedAttributeIds,
                materials: {
                    ...prev.materials,
                    ...syncedMaterials,
                },
                labor: {
                    ...prev.labor,
                    ...syncedLabor,
                },
                cartIds: syncedCartIds,
            }));

            setChangeIdentified(false);
            showSuccess("Changes saved successfully");
            return { success: true };
        } catch (err) {
            console.error("[EstimateContract] sync failed", err);
            showError("Failed to save changes");
            return { success: false };
        } finally {
            setSyncing(false);
        }
    }

    return { changeIdentified, flushSave, syncing };
}

type MeasurementEntry = { Calculated?: number; Actual?: number };

function normalizeScalarAttribute(value: unknown): string {
    if (value == null || value === 0) return "";
    return String(value);
}

function hasScalarAttributeChanges(
    current: Record<string, unknown> = {},
    reference: Record<string, unknown> = {},
): boolean {
    return SYNCABLE_SCALAR_ATTRIBUTES.some(
        (key) =>
            normalizeScalarAttribute(current[key]) !==
            normalizeScalarAttribute(reference[key]),
    );
}

function identifyScalarAttributeChanges(
    current: Record<string, unknown> = {},
    reference: Record<string, unknown> = {},
): Record<string, { from: string; to: string }> {
    const changes: Record<string, { from: string; to: string }> = {};
    for (const key of SYNCABLE_SCALAR_ATTRIBUTES) {
        const from = normalizeScalarAttribute(reference[key]);
        const to = normalizeScalarAttribute(current[key]);
        if (from !== to) {
            changes[key] = { from, to };
        }
    }
    return changes;
}

function isMeasurementEqual(
    current?: MeasurementEntry,
    reference?: MeasurementEntry,
) {
    return (
        Number(current?.Calculated) === Number(reference?.Calculated) &&
        Number(current?.Actual) === Number(reference?.Actual)
    );
}

function hasMeasurementChanges(
    current: Record<string, any> = {},
    reference: Record<string, any> = {},
) {
    const keys = Array.from(
        new Set([...Object.keys(current), ...Object.keys(reference)]),
    );

    return keys.some(
        (key) => !isMeasurementEqual(current[key], reference[key]),
    );
}

function identifyMeasurementChanges(
    current: Record<string, MeasurementEntry> = {},
    reference: Record<string, MeasurementEntry> = {},
) {
    const changes: Record<
        string,
        { from: MeasurementEntry | undefined; to: MeasurementEntry | undefined }
    > = {};
    const keys = Array.from(
        new Set([...Object.keys(current), ...Object.keys(reference)]),
    );

    for (const key of keys) {
        const from = reference[key];
        const to = current[key];
        if (!isMeasurementEqual(to, from)) {
            changes[key] = { from, to };
        }
    }

    return changes;
}

function cloneMeasurement(entry?: MeasurementEntry) {
    return {
        Calculated: Number(entry?.Calculated) || 0,
        Actual: Number(entry?.Actual) || 0,
    };
}
